import { ref, computed } from 'vue'
import store from '@/store'
import tenantInfoService from '@/services/tenantInfoService'

/**
 * Profile completion for the signed-in tenant.
 *
 * Module-level state, not per-component: the sidebar badge, the profile page card and
 * the warning banner all read the same figures, and fetching once keeps them from
 * disagreeing or issuing three identical requests on every navigation.
 */
const completion = ref(null)
const loading = ref(false)
const loaded = ref(false)

export function useProfileCompletion() {
  const load = async (force = false) => {
    // A forced refresh must never be dropped: it is called right after a save, and
    // skipping it because an unrelated fetch happens to be in flight would leave the
    // figure stale until the next page load — the bug this guard used to cause.
    if (loading.value && !force) return completion.value
    if (loaded.value && !force) return completion.value

    loading.value = true
    try {
      // The tenant id is passed explicitly. ABP's conventional API builds the route from
      // the method signature, and an optional Guid? still becomes a required path
      // segment — calling without it 404s, exactly as the sibling entity-counts endpoint
      // does. Sending the id keeps this off that behaviour entirely.
      const tenantId = store.getters['auth/currentUser']?.tenantId || null
      completion.value = await tenantInfoService.getProfileCompletion(tenantId)
      loaded.value = true
    } catch {
      // Non-critical: a missing score must not break the screens that show it.
      completion.value = null
    } finally {
      loading.value = false
    }
    return completion.value
  }

  /** Call after saving profile data so the figures do not go stale. */
  const refresh = () => load(true)

  const isIncomplete = computed(() => completion.value != null && !completion.value.isComplete)
  const percentage = computed(() => completion.value?.completionPercentage ?? 0)
  const sectionCounts = computed(() => completion.value?.sectionCounts ?? null)

  return { completion, loading, load, refresh, isIncomplete, percentage, sectionCounts }
}
